import * as React from "react"
import { cn } from "@/lib/utils"
import { Input } from "./input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./dialog"
import { DialogActions } from "./dialog-actions"
import { Icon } from "@/components/ui/icon"

export function generateTypeToConfirmToken(length = 3): string {
  let token = ""
  for (let i = 0; i < length; i += 1) {
    token += String(Math.floor(Math.random() * 10))
  }
  return token
}

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: "danger" | "warning" | "default"
  isLoading?: boolean
  /** Quando omitido em danger/warning, um código numérico é gerado ao abrir. */
  confirmToken?: string
}

const variantConfig = {
  danger: {
    icon: "delete_forever",
    iconBg: "bg-destructive/10",
    iconColor: "text-destructive",
    buttonVariant: "destructive" as const,
    buttonClass: undefined as string | undefined,
  },
  warning: {
    icon: "warning",
    iconBg: "bg-warning/10",
    iconColor: "text-warning",
    buttonVariant: "default" as const,
    buttonClass: "bg-warning hover:bg-warning/90 text-warning-foreground" as string | undefined,
  },
  default: {
    icon: "help",
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    buttonVariant: "default" as const,
    buttonClass: undefined as string | undefined,
  },
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  variant = "default",
  isLoading = false,
  confirmToken: confirmTokenProp,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState("")
  const [internalToken, setInternalToken] = React.useState("")
  const requiresTyping = variant === "danger" || variant === "warning"
  const confirmToken = confirmTokenProp ?? internalToken
  const matches = !requiresTyping || typed.trim() === confirmToken
  const styles = variantConfig[variant]

  React.useEffect(() => {
    if (!isOpen) return
    setTyped("")
    if (requiresTyping && !confirmTokenProp) {
      setInternalToken(generateTypeToConfirmToken())
    }
  }, [isOpen, requiresTyping, confirmTokenProp])

  React.useEffect(() => {
    if (isOpen) setTyped("")
  }, [isOpen, confirmToken])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent
        size={requiresTyping ? "sm" : undefined}
        layout={requiresTyping ? "flex" : undefined}
        className={cn(
          "gap-0 overflow-y-auto overscroll-contain p-0",
          !requiresTyping && "max-w-md")}
      >
        <DialogHeader className="p-6 pb-0 pr-14 text-center items-center">
          <div
            className={cn(
              "w-16 h-16 rounded-full flex items-center justify-center mb-4",
              styles.iconBg
            )}
          >
            <Icon name={styles.icon} className={cn(styles.iconColor)} />
          </div>
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center">{description}</DialogDescription>
        </DialogHeader>

        {requiresTyping ? (
          <div className="flex flex-col gap-3 px-6 py-4">
            <p className="text-center text-sm text-muted-foreground">
              Digite{" "}
              <span className="font-mono font-bold tracking-widest text-foreground">
                {confirmToken}
              </span>{" "}
              para confirmar
            </p>
            <Input
              value={typed}
              onChange={(e) =>
                setTyped(e.target.value.replace(/\D/g, "").slice(0, confirmToken.length))
              }
              placeholder={confirmToken}
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={isLoading}
              className="text-center font-mono tracking-widest"
              aria-label="Código de confirmação"
            />
          </div>
        ) : null}

        <DialogActions
          confirmLabel={confirmText}
          onConfirm={onConfirm}
          cancelLabel={cancelText}
          onCancel={onClose}
          confirmVariant={styles.buttonVariant}
          confirmClassName={styles.buttonClass}
          loading={isLoading}
          disabled={!matches}
          cancelDisabled={isLoading}
        />
      </DialogContent>
    </Dialog>
  )
}

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = React.useState(false)
  const [confirmToken, setConfirmToken] = React.useState("")
  const [config, setConfig] = React.useState<{
    title: string
    description: string
    variant: "danger" | "warning" | "default"
    confirmText?: string
    cancelText?: string
    onConfirm: () => Promise<void> | void
  } | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  const open = (options: {
    title: string
    description: string
    variant?: "danger" | "warning" | "default"
    confirmText?: string
    cancelText?: string
    onConfirm: () => Promise<void> | void
  }) => {
    const variant = options.variant || "default"
    if (variant === "danger" || variant === "warning") {
      setConfirmToken(generateTypeToConfirmToken())
    } else {
      setConfirmToken("")
    }
    setConfig({
      ...options,
      variant,
    })
    setIsOpen(true)
  }

  const close = () => {
    if (!isLoading) {
      setIsOpen(false)
      setConfig(null)
      setConfirmToken("")
    }
  }

  const handleConfirm = async () => {
    if (config?.onConfirm) {
      setIsLoading(true)
      try {
        await config.onConfirm()
        close()
      } finally {
        setIsLoading(false)
      }
    }
  }

  return {
    isOpen,
    config,
    confirmToken,
    isLoading,
    open,
    close,
    handleConfirm,
  }
}
